import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './styles/PublicUpload.css';

const PublicUpload = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('validating'); // validating | valid | invalid | uploading | success | error
  const [hasPassword, setHasPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`/api/files/upload-link/${token}/validate`)
      .then(({ data }) => {
        if (data.valid) {
          setStatus('valid');
          setHasPassword(data.hasPassword);
        } else {
          setStatus('invalid');
        }
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const doUpload = () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    if (hasPassword) fd.append('password', password);

    setStatus('uploading');
    setProgress(0);
    setErrorMsg('');

    axios.post(`/api/files/upload-link/${token}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        setProgress(Math.round((e.loaded * 100) / e.total));
      },
    })
    .then(({ data }) => {
      setStatus('success');
      setFileName(data.fileName);
    })
    .catch((err) => {
      const msg = err.response?.data?.message || 'Upload failed';
      setErrorMsg(msg);
      // If password was wrong, go back to valid state so they can retry
      if (err.response?.status === 401) {
        setStatus('valid');
      } else {
        setStatus('error');
      }
    });
  };

  const formatSize = (bytes) => {
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
  };

  return (
    <div className="public-upload-page">
      <div className="public-upload-card">
        <h2>Secure File Upload</h2>
        <p className="public-upload-subtitle">soluminternal.com</p>

        {status === 'validating' && (
          <div className="pu-status">Validating link...</div>
        )}

        {status === 'invalid' && (
          <div className="pu-status pu-error">
            This upload link is invalid, expired, or has already been used.
          </div>
        )}

        {status === 'valid' && (
          <>
            {errorMsg && <div className="pu-status pu-error">{errorMsg}</div>}

            <div
              className="pu-drop-zone"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="pu-file-info">
                  <span className="pu-file-name">{file.name}</span>
                  <span className="pu-file-size">{formatSize(file.size)}</span>
                  <button className="pu-remove" onClick={(e) => { e.stopPropagation(); setFile(null); }}>Remove</button>
                </div>
              ) : (
                <p>Drag & drop a file here, or click to choose</p>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }}
            />

            {hasPassword && (
              <div className="pu-password-row">
                <label>Password:</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
              </div>
            )}

            <button
              className="pu-upload-btn"
              onClick={doUpload}
              disabled={!file || (hasPassword && !password)}
            >
              Upload
            </button>
          </>
        )}

        {status === 'uploading' && (
          <div className="pu-uploading">
            <p>Uploading... {progress}%</p>
            <div className="pu-progress-container">
              <div className="pu-progress-bar" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="pu-status pu-success">
            File "{fileName}" uploaded successfully. You can close this page.
          </div>
        )}

        {status === 'error' && (
          <div className="pu-status pu-error">
            {errorMsg || 'Upload failed. The link may have expired or already been used.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicUpload;
